import $ from 'jquery';
import 'bootstrap/js/dist/collapse.js';
import ValidatedFormView from '../form/ValidatedFormView.js';
import LoadingIndicator from '../ui/LoadingIndicator.mjs';

const SELECTOR_INPUT_WITH_SHOW_CONDITION = '[data-show-condition]';
const SELECTOR_FILE_UPLOAD_HOLDER = '.form-group-upload-holder';
const RULE_REQUIRED = 'required';
const RULE_OPTIONAL = 'optional';
const RULE_ALWAYS_INVALID = 'isAlwaysInvalid';

const errorModal = $('#ef-job-application-form-error-modal');
if (errorModal.length) {
  errorModal.modal('show');
}
export class JobApplicationForm extends ValidatedFormView {
  constructor(options) {
    super(Object.assign({
      formName: 'JobApplicationForm',
    }, options));

    // Add validations dynamically
    this.validations = this.generateValidations();
    this.attachValidations();
    this.liveValidations = this.generateLiveValidations();
    this.attachLiveValidations();

    this.initializeMultipleUploadHandling();
    this.disableHtmlFormValidation();
    this.scrollToErrorMessage();
  }

  generateValidations() {
    const $allFormFields = this.$form.find('input, textarea, select');
    let validations = {};

    $allFormFields.each((index, field) => {
      const $field = $(field);
      const name = $field.attr('name');
      const type = $field.attr('type');
      const maxLength = $field.attr('maxlength');
      const isRequired = !!$field.attr('required');
      const isConditional = !!$field.attr('data-show-condition');

      if (type === 'hidden' || isConditional) {
        // Ignoring hidden and conditional fields
        return;
      }

      const rules = [];

      const addRule = (rule) => rules.push(rule);
      const removeRule = (rule) => rules.splice(rules.indexOf(rule), 1);
      const addMaxLength = () => maxLength &&
        addRule({rule: 'maxLength', param: maxLength});

      if (!isRequired) {
        addRule(RULE_OPTIONAL);
        addMaxLength();
      } else {
        switch (type) {
          case 'checkbox':
            const isCheckboxGroup = !!$field.attr('data-checkbox-group');
            if (isCheckboxGroup) {
              addRule({rule: 'atLeastIsOneCheckedInGroup', param: name});
            } else {
              addRule({rule: 'checked', param: name});
            }
            break;
          case 'email':
            addRule(RULE_REQUIRED);
            addRule('email');
            addMaxLength();
            break;
          case 'date':
            addRule(RULE_REQUIRED);
            addRule('dateNotInFuture');
            break;
          case 'file':
            const $fileUpload = $field.
              closest('.form-group').
              find(SELECTOR_FILE_UPLOAD_HOLDER);
            if ($fileUpload.length === 0) {
              addRule(RULE_REQUIRED);
            }
            break;
          case 'tel':
            addRule(RULE_REQUIRED);
            addRule('phone');
            addMaxLength();
            break;
          default:
            addRule(RULE_REQUIRED);
            addMaxLength();
        }
      }

      // Edge case for errors from server side validation
      if (type === 'file' && $field.closest('.form-group').hasClass('has-error')) {
        removeRule(RULE_OPTIONAL);
        addRule(RULE_REQUIRED);
        addRule(RULE_ALWAYS_INVALID);
      }

      if (rules.length > 0) {
        validations[name] = rules;
      }
    });

    return validations;
  }

  generateLiveValidations() {
    const liveValidations = {};

    this.handleFileInputs(liveValidations);
    this.handleInputsWithDisplayCondition(liveValidations);

    return liveValidations;
  }

  handleFileInputs(liveValidations) {
    const fileUploadTemplate = $('#form-jobApplication-template-file-upload').
      html();

    this.$form.find('input[type="file"]').each((index, field) => {
      const $field = $(field);
      const $formGroup = $field.closest('.form-group');
      const fieldName = $field.attr('name');
      const $existingFileUpload = $formGroup.
        find(SELECTOR_FILE_UPLOAD_HOLDER).
        first();
      let $hiddenField;

      $existingFileUpload.find('button').on(
        'click',
        removeFileUpload.bind(this, $existingFileUpload),
      );

      liveValidations[fieldName] = {
        'change': [
          () => {
            if ($field[0]?.files.length === 0) {
              // Don't do anything if no file is selected
              return;
            }
            checkFileSize.call(this);
            addOrUpdateFileUpload.call(this);
          },
        ],
      };

      /**
       * Check if file size is maximum what the server allows
       * @returns {boolean}
       */
      function checkFileSize() {
        const isRequired = !!$field.attr(RULE_REQUIRED);
        const fileSize = $field[0]?.files[0]?.size || 0;
        const maxFileSize = $field.data('maxFileSize') || 0;
        const isFileSizeValid = maxFileSize === 0 || fileSize <= maxFileSize;

        if (!isFileSizeValid) {
          // Force invalid state, even for optional fields
          this.addValidation(fieldName, isRequired ?
            [RULE_REQUIRED, RULE_ALWAYS_INVALID] :
            [RULE_OPTIONAL, RULE_ALWAYS_INVALID]);
          this.validateField(fieldName, isRequired ?
            [RULE_REQUIRED, RULE_ALWAYS_INVALID] :
            [RULE_OPTIONAL, RULE_ALWAYS_INVALID]);

          addErrorIndicatorToCollapse();
        } else {
          // Remove invalid state
          this.addValidation(fieldName,
            isRequired ? [RULE_REQUIRED] : [RULE_OPTIONAL]);
          this.validateField(fieldName,
            isRequired ? [RULE_REQUIRED] : [RULE_OPTIONAL]);

          removeErrorIndicatorFromCollapse();
        }

        return isFileSizeValid;
      }

      /**
       * Add or update file upload button
       */
      function addOrUpdateFileUpload() {
        const $oldFormUpload = $formGroup.
          find(SELECTOR_FILE_UPLOAD_HOLDER).
          first();
        const $newFileUpload = $(
          fileUploadTemplate.replace(/#fileName#/g, $field[0].files[0].name),
        );

        if ($oldFormUpload.length) {
          // Replace old with new file
          $oldFormUpload.replaceWith($newFileUpload);
        } else {
          // Add new file
          $formGroup.
            find('.form-group-upload-wrapper').
            first().
            append($newFileUpload);
        }

        // Remove hidden field, so that the server updates the file
        $hiddenField?.remove();
        $newFileUpload.find('button').on('click', removeFileUpload.bind(this, $newFileUpload));
      }

      /**
       * Remove current file upload and add hint for the server
       * @param {jQuery} $currentFileUpload
       */
      function removeFileUpload($currentFileUpload) {
        const isRequired = !!$field.attr(RULE_REQUIRED);

        $currentFileUpload.trigger('jobApplicationForm:removeFileUpload');

        // Remove file
        $currentFileUpload.remove();

        // Clear input
        $field.val('');
        // Necessary to trigger change when came back from check action, since input is already empty
        $field.trigger('change');

        // Add hidden field, which submit an empty value to remove file on server
        $hiddenField = $field.clone();
        $hiddenField.attr('type', 'hidden');
        $formGroup.append($hiddenField);

        this.addValidation(fieldName, isRequired ? [RULE_REQUIRED] : [RULE_OPTIONAL]);
        this.validateField(fieldName, isRequired ? [RULE_REQUIRED] : [RULE_OPTIONAL]);

        removeErrorIndicatorFromCollapse();
      }

      function addErrorIndicatorToCollapse() {
        $formGroup.closest('.form-collapse').addClass('has-sub-error');
      }

      function removeErrorIndicatorFromCollapse() {
        // Remove error indicator if no other upload in the group is invalid
        if ($formGroup.closest('.form-group-file').find('.has-error').length === 0) {
          $formGroup.closest('.form-collapse').removeClass('has-sub-error');
        }
      }
    });
  }

  initializeMultipleUploadHandling() {
    const $addUploadBtn = this.$form.find('#form-jobApplication-add-upload');

    setupCollapseEventHandler($addUploadBtn);

    /** @param {jQuery} $btn */
    function setupCollapseEventHandler($btn) {
      const $targetCollapse = $($btn.attr('data-target'));

      $targetCollapse.on('show.bs.collapse', () => {
        const highestIndex = $btn.data('indexHighest');
        const currentIndex = $btn.data('indexCurrent');
        const newIndex = currentIndex + 1;

        if (newIndex > highestIndex) {
          // Hide button if we've reached the maximum number of uploads
          const $ownCollapse = $btn.closest('.collapse');
          $ownCollapse.collapse('hide');
          return;
        }

        // Update button attributes for next upload
        const previousAriaControls = $btn.attr('aria-controls');
        const newDataAriaControls = previousAriaControls.replace(/\d+$/, newIndex);
        $btn.data('indexCurrent', newIndex);
        $btn.attr({
          'data-index-current': newIndex,
          'aria-expanded': 'false',
          'data-target': '#' + newDataAriaControls,
          'aria-controls': newDataAriaControls
        });

        // Re-attach handler for next upload instance
        setupCollapseEventHandler($btn);
      });
    }
  }

  handleInputsWithDisplayCondition(liveValidations) {
    // Add conditional fields (display and validation)
    this.$form.find(SELECTOR_INPUT_WITH_SHOW_CONDITION).each((index, field) => {
      const $conditionalField = $(field);
      const conditionalFieldName = $conditionalField.attr('name');
      const condition = $conditionalField.data('showCondition') || false;

      if (!condition) {
        return;
      }

      const $collapse = $conditionalField.closest('.collapse');
      const $relatedField = this.$form.find(`[name="${condition.field}"]`);
      const relatedFieldName = $relatedField.attr('name');

      // Initially show, if necessary
      if ($relatedField.val().startsWith(condition.valueStartsWith)) {
        $collapse.collapse('show');
        this.addValidation(conditionalFieldName, [RULE_REQUIRED]);
      }

      liveValidations[relatedFieldName] = {
        'change': [
          () => {
            if ($relatedField.val().startsWith(condition.valueStartsWith)) {
              $collapse.collapse('show');
              this.addValidation(conditionalFieldName, [RULE_REQUIRED]);
            } else {
              $collapse.collapse('hide');
              this.removeValidation(conditionalFieldName);
              $conditionalField.val('');
            }
          },
        ],
      };
    });
  }

  disableHtmlFormValidation() {
    this.$form[0].noValidate = true;
  }

  doSubmit(event) {
    LoadingIndicator.show()
    super.doSubmit(event);
  }
}
